import { useCallback, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FileInfo, UploadLimits } from '../types';
import { downloadFile, validateFilename } from '../utils/fileList';

const API_URL = process.env.REACT_APP_API_URL;

interface RenameMutationVariables {
  fileId: number;
  filename: string;
}

export const useFileListPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [openDialog, setOpenDialog] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [fileToDelete, setFileToDelete] = useState<number | null>(null);
  const [isDownloadingId, setIsDownloadingId] = useState<number | null>(null);
  const [editingFileId, setEditingFileId] = useState<number | null>(null);
  const [editedFilename, setEditedFilename] = useState('');
  const [renameError, setRenameError] = useState<string | null>(null);

  const uploadLimitsQuery = useQuery<UploadLimits>({
    queryKey: ['uploadLimits'],
    queryFn: async () => {
      const response = await axios.get(`${API_URL}/api/files/upload-limits`);
      return response.data;
    },
  });

  const filesQuery = useQuery<FileInfo[]>({
    queryKey: ['files'],
    queryFn: async () => {
      const response = await axios.get(`${API_URL}/api/files`);
      return response.data;
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (fileId: number) => {
      await axios.delete(`${API_URL}/api/files/${fileId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['files'] });
      queryClient.invalidateQueries({ queryKey: ['uploadLimits'] });
      setOpenDialog(false);
      setFileToDelete(null);
      setDeleteError(null);
    },
    onError: (error: unknown) => {
      if (axios.isAxiosError(error)) {
        const detail = error.response?.data?.detail;
        setDeleteError(
          typeof detail === 'string' ? detail : 'ファイルの削除に失敗しました。',
        );
      } else {
        setDeleteError('ファイルの削除に失敗しました。');
      }
    },
  });

  const renameMutation = useMutation({
    mutationFn: async ({ fileId, filename }: RenameMutationVariables) => {
      await axios.patch(`${API_URL}/api/files/${fileId}`, { filename });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['files'] });
      setEditingFileId(null);
      setEditedFilename('');
      setRenameError(null);
    },
    onError: (error: unknown) => {
      if (axios.isAxiosError(error)) {
        const detail = error.response?.data?.detail;
        setRenameError(
          typeof detail === 'string' ? detail : 'ファイル名の更新に失敗しました。',
        );
      } else {
        setRenameError('ファイル名の更新に失敗しました。');
      }
    },
  });

  const currentUsageMb = uploadLimitsQuery.data?.current_total_upload_size_mb ?? 0;
  const maxTotalMb = uploadLimitsQuery.data?.max_total_upload_size_mb ?? 0;
  const maxUploadFiles = uploadLimitsQuery.data?.max_upload_files ?? 0;

  const currentFileCount = filesQuery.data?.length ?? 0;
  const queryError = useMemo(() => {
    if (!filesQuery.error) return null;
    return filesQuery.error instanceof Error
      ? filesQuery.error
      : new Error(String(filesQuery.error));
  }, [filesQuery.error]);

  const targetFileToDelete = useMemo(
    () => filesQuery.data?.find((file) => file.id === fileToDelete) ?? null,
    [filesQuery.data, fileToDelete],
  );

  const handleViewDashboard = useCallback(
    (fileId: number) => {
      navigate(`/dashboard/${fileId}`);
    },
    [navigate],
  );

  const handleOpenDeleteDialog = useCallback((fileId: number) => {
    setFileToDelete(fileId);
    setOpenDialog(true);
  }, []);

  const handleCloseDeleteDialog = useCallback(() => {
    setOpenDialog(false);
    setDeleteError(null);
    setFileToDelete(null);
  }, []);

  const handleDeleteFile = useCallback(() => {
    if (deleteMutation.isPending) {
      return;
    }
    if (fileToDelete !== null) {
      setDeleteError(null);
      deleteMutation.mutate(fileToDelete);
    }
  }, [deleteMutation, fileToDelete]);

  const handleStartRename = useCallback((file: FileInfo) => {
    setEditingFileId(file.id);
    setEditedFilename(file.filename);
    setRenameError(null);
  }, []);

  const handleCancelRename = useCallback(() => {
    setEditingFileId(null);
    setEditedFilename('');
    setRenameError(null);
  }, []);

  const handleFilenameChange = useCallback((value: string) => {
    setEditedFilename(value);
    if (renameError) {
      setRenameError(null);
    }
  }, [renameError]);

  const handleConfirmRename = useCallback(() => {
    if (editingFileId === null) return;
    const result = validateFilename(editedFilename);
    if (!result.valid) {
      setRenameError(result.error ?? 'ファイル名を入力してください。');
      return;
    }
    renameMutation.mutate({ fileId: editingFileId, filename: result.filename });
  }, [editedFilename, editingFileId, renameMutation]);

  const handleDownload = useCallback(
    async (fileId: number, originalFilename: string) => {
      setIsDownloadingId(fileId);
      try {
        await downloadFile({
          fileId,
          originalFilename,
          baseUrl: API_URL ?? '',
          axiosInstance: axios,
        });
      } catch (error) {
        console.error('Download error:', error);
        alert('ファイルのダウンロードに失敗しました。');
      } finally {
        setIsDownloadingId(null);
      }
    },
    [],
  );

  return useMemo(
    () => ({
      files: filesQuery.data ?? [],
      isLoading: filesQuery.isLoading,
      isFetching: filesQuery.isFetching,
      error: queryError,
      uploadSummary: {
        currentUsageMb,
        maxTotalMb,
        maxUploadFiles,
        currentFileCount,
      },
      uploadLimitsState: {
        status: uploadLimitsQuery.isError
          ? 'error'
          : uploadLimitsQuery.data
            ? 'loaded'
            : 'loading',
        isFetching: uploadLimitsQuery.isFetching,
      },
      deleteDialog: {
        open: openDialog,
        targetFileId: fileToDelete,
        targetFilename: targetFileToDelete?.filename ?? '',
        error: deleteError,
      },
      renameState: {
        editingFileId,
        editedFilename,
        renameError,
        isPending: renameMutation.isPending,
      },
      downloadState: {
        isDownloadingId,
      },
      mutations: {
        delete: deleteMutation,
        rename: renameMutation,
      },
      actions: {
        refetchFiles: filesQuery.refetch,
        refetchUploadLimits: uploadLimitsQuery.refetch,
        viewDashboard: handleViewDashboard,
        openDeleteDialog: handleOpenDeleteDialog,
        closeDeleteDialog: handleCloseDeleteDialog,
        deleteFile: handleDeleteFile,
        startRename: handleStartRename,
        cancelRename: handleCancelRename,
        changeFilename: handleFilenameChange,
        confirmRename: handleConfirmRename,
        downloadFile: handleDownload,
      },
    }),
    [
      filesQuery.data,
      filesQuery.isLoading,
      filesQuery.isFetching,
      queryError,
      currentUsageMb,
      maxTotalMb,
      maxUploadFiles,
      currentFileCount,
      uploadLimitsQuery.data,
      uploadLimitsQuery.isError,
      uploadLimitsQuery.isFetching,
      uploadLimitsQuery.refetch,
      openDialog,
      fileToDelete,
      targetFileToDelete,
      deleteError,
      editingFileId,
      editedFilename,
      renameError,
      isDownloadingId,
      deleteMutation,
      renameMutation,
      handleViewDashboard,
      handleOpenDeleteDialog,
      handleCloseDeleteDialog,
      handleDeleteFile,
      handleStartRename,
      handleCancelRename,
      handleFilenameChange,
      handleConfirmRename,
      handleDownload,
      filesQuery.refetch,
    ],
  );
};
